import React, { useState } from "react";
import { CloseIcon } from "@/assets/icons";
import Button, { ButtonVariant } from "@/ui/Button/Button";
import Checkbox from "@/ui/Checkbox/Checkbox";
import ChipsSelect from "@/ui/ChipsSelect/ChipsSelect";
import DropdownSelector from "@/ui/DropdownSelector/DropdownSelector";
import Input from "@/ui/Input/Input";
import Modal from "@/ui/Modal/Modal";
import Panel from "@/ui/Panel/Panel";
import Tooltip from "@/ui/Tooltip/Tooltip";
import Typography, { TypographySize } from "@/ui/Typography/Typography";
import { OptionI } from "@/utils/types";
import cls from "./styles.module.scss";

const options: OptionI[] = [
  { value: "option-1", label: "Option 1" },
  { value: "option-2", label: "Option 2" },
  { value: "option-3", label: "Option 3" },
  { value: "option-4", label: "Option 4" },
  { value: "option-5", label: "Option 5" },
];

const typographySizes: TypographySize[] = [
  "7xl",
  "6xl",
  "5xl",
  "4xl",
  "3xl",
  "2xl",
  "xl",
  "lg",
  "md",
  "sm",
  "xs",
];

const buttonVariants: ButtonVariant[] = [
  "primary",
  "secondary",
  "success",
  "outlined",
  "ghost",
  "dark",
  "light",
  "text",
  "danger",
  "iconOnly",
];

const ButtonColumn = ({ variant }: { variant: ButtonVariant }) => {
  const icon = <CloseIcon />;

  return (
    <div className={cls.buttonColumn}>
      <Typography size="lg" weight="semibold">
        {variant}
      </Typography>
      <Button
        onClick={() => null}
        variant={variant}
        icon={variant === "iconOnly" ? icon : undefined}
        label="Button"
      />
      <Button
        onClick={() => null}
        variant={variant}
        icon={variant === "iconOnly" ? icon : undefined}
        label="Button"
        disabled
      />
      <Button
        onClick={() => null}
        variant={variant}
        icon={variant === "iconOnly" ? icon : undefined}
        label="Button"
        loading
      />
      <Button onClick={() => null} variant={variant} label="Button" icon={icon} />
      <Button
        onClick={() => null}
        variant={variant}
        label="Button"
        icon={icon}
        disabled
      />
      <Button
        onClick={() => null}
        variant={variant}
        label="Button"
        icon={icon}
        loading
      />
    </div>
  );
};

export const PlaygroundPage = () => {
  const [textValue, setTextValue] = useState("Template value");
  const [passwordValue, setPasswordValue] = useState("password");
  const [dropdownValue, setDropdownValue] = useState(options[0].value);
  const [chipsValue, setChipsValue] = useState<OptionI[]>([
    options[0],
    options[1],
  ]);
  const [checked, setChecked] = useState(true);
  const [unchecked, setUnchecked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <main className={cls.wrapper}>
      <section className={cls.header}>
        <Typography component="h1" size="4xl" weight="bold">
          UI playground
        </Typography>
        <Typography color="var(--text-dark-secondary)">
          Default components with common states for template development.
        </Typography>
      </section>

      <Panel className={cls.section}>
        <Typography component="h2" size="2xl" weight="semibold">
          Typography
        </Typography>
        <div className={cls.typeGrid}>
          {typographySizes.map((size) => (
            <Typography key={size} size={size}>
              Example Text 123 | {size}
            </Typography>
          ))}
        </div>
      </Panel>

      <Panel className={cls.section}>
        <Typography component="h2" size="2xl" weight="semibold">
          Buttons
        </Typography>
        <div className={cls.buttonGrid}>
          <div className={cls.buttonColumn}>
            <Typography size="lg" weight="semibold">
              State
            </Typography>
            <Typography>Default</Typography>
            <Typography>Disabled</Typography>
            <Typography>Loading</Typography>
            <Typography>With icon</Typography>
            <Typography>With icon disabled</Typography>
            <Typography>With icon loading</Typography>
          </div>
          {buttonVariants.map((variant) => (
            <ButtonColumn key={variant} variant={variant} />
          ))}
        </div>
      </Panel>

      <div className={cls.grid}>
        <Panel className={cls.section}>
          <Typography component="h2" size="2xl" weight="semibold">
            Inputs
          </Typography>
          <Input
            label="Default"
            value={textValue}
            onChange={setTextValue}
            placeholder="Type here"
            hint="Helpful hint text"
          />
          <Input
            label="Password"
            type="password"
            value={passwordValue}
            onChange={setPasswordValue}
          />
          <Input
            label="Error"
            value="Invalid value"
            onChange={() => null}
            error="This field needs attention"
          />
          <Input
            label="Disabled"
            value="Disabled value"
            onChange={() => null}
            disabled
          />
        </Panel>

        <Panel className={cls.section}>
          <Typography component="h2" size="2xl" weight="semibold">
            Selectors
          </Typography>
          <ChipsSelect
            options={options}
            selected={chipsValue}
            onChange={setChipsValue}
            maxVisibleChips={2}
          />
          <DropdownSelector
            label="Dropdown"
            options={options}
            value={dropdownValue}
            onChange={setDropdownValue}
          />
          <DropdownSelector
            label="Dropdown with error"
            options={options}
            value=""
            onChange={() => null}
            placeholder="Select option"
            error="Select one option"
          />
        </Panel>

        <Panel className={cls.section}>
          <Typography component="h2" size="2xl" weight="semibold">
            Checkbox
          </Typography>
          <Checkbox checked={checked} onChange={setChecked} label="Checked" />
          <Checkbox
            checked={unchecked}
            onChange={setUnchecked}
            label="Unchecked"
          />
          <Checkbox
            checked={false}
            indeterminate
            onChange={() => null}
            label="Indeterminate"
          />
          <Checkbox checked disabled onChange={() => null} label="Disabled" />
        </Panel>

        <Panel className={cls.section}>
          <Typography component="h2" size="2xl" weight="semibold">
            Tooltip and modal
          </Typography>
          <div className={cls.inline}>
            <Tooltip text="Tooltip on top" position="top">
              <Button label="Top tooltip" variant="outlined" onClick={() => null} />
            </Tooltip>
            <Tooltip text="Tooltip on right" position="right">
              <Button
                label="Right tooltip"
                variant="outlined"
                onClick={() => null}
              />
            </Tooltip>
          </div>
          <Button label="Open modal" onClick={() => setModalOpen(true)} />
        </Panel>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div className={cls.modalBody}>
          <Typography component="h2" size="2xl" weight="semibold">
            Modal title
          </Typography>
          <Typography color="var(--text-dark-secondary)">
            This is the default modal surface with close behavior and CSS
            animation.
          </Typography>
          <Button label="Close" onClick={() => setModalOpen(false)} />
        </div>
      </Modal>
    </main>
  );
};
